import {
  Divider,
  Typography,
  Link,
  Form,
  TextField,
  Button,
  ButtonGroup,
  useEmailAddressValidator,
} from '@ace-clublabs/shared-component-library';
import React, { useState } from 'react';

interface Props {
  handleContinue: (values: any) => void;
  primaryEmail?: string | undefined;
}

const UpdateEmailsForm: React.FC<Props> = ({ handleContinue, primaryEmail }) => {
  const [_primaryEmail, setPrimaryEmail] = useState('');
  const [secondaryEmail, setSecondaryEmail] = useState('');

  const headerText = primaryEmail
    ? 'We will send a confirmation email to the address below'
    : 'Enter an email address where we can send you a confirmation';

  const regex = '(^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$)|$^';

  const getContinueButtonDisabled = () => {
    const secondaryEmailValid = new RegExp(regex).test(secondaryEmail);
    if (!primaryEmail) {
      const primaryEmailValid = new RegExp(regex).test(_primaryEmail);
      const noPrimaryWithSecondary = !_primaryEmail && !secondaryEmail;
      return noPrimaryWithSecondary || !primaryEmailValid || !secondaryEmailValid;
    }
    return !secondaryEmailValid;
  };

  const handleLinkClick = (url: string) => (event: any) => {
    event.preventDefault();
    window.open(url);
  };

  return (
    <div id="update-emails-form">
      <Typography
        variant="h5"
        component="h1"
        id="update-emails-form-header"
        sx={{ fontWeight: '500', marginTop: '20px' }}
      >
        {headerText}
      </Typography>
      <Form onSubmit={handleContinue}>
        {primaryEmail ? (
          <TextField
            id="primary-email-disabled"
            label="Enter email address (optional)"
            value={primaryEmail}
            className="email-input"
            fullWidth
            disabled
          />
        ) : (
          <TextField
            id="primary-email"
            label="Enter email address (optional)"
            className="email-input"
            fullWidth
            onChange={(e: any) => setPrimaryEmail(e.target.value)}
            validator={useEmailAddressValidator({ label: 'Email address', required: false })}
          />
        )}
        <Divider />
        <Typography variant="b1Medium" id="secondary-email-header">
          Add another recipient
        </Typography>
        <TextField
          id="secondary-email"
          label="Enter email address (optional)"
          className="email-input"
          fullWidth
          onChange={(e: any) => setSecondaryEmail(e.target.value)}
          validator={useEmailAddressValidator({ label: 'Email address', required: false })}
        />
        <ButtonGroup id="emails-button-group">
          <Button
            id="emails-continue"
            onClick={() => handleContinue({ primaryEmail: _primaryEmail, secondaryEmail })}
            disabled={getContinueButtonDisabled()}
            label="Continue"
          />
          <Button id="skip" variant="outlined" onClick={handleContinue} label="Or, skip" />
        </ButtonGroup>
      </Form>
      <Typography variant="b2Regular" id="privacy-policy-text" className="body-privacy-text">
        I agree to calls and texts, using automated technology, from or on AAA's behalf regarding membership
        products/services and offers. Consent is not a condition of purchase. Message and data rates may apply. I agree
        to the{' '}
        <Link
          id="privacy-policy-link"
          component="a"
          onClick={handleLinkClick('https://www.ace.aaa.com/legal/privacy.html')}
        >
          Privacy Policy
        </Link>
        ,{' '}
        <Link
          id="terms-and-conditions-link"
          component="a"
          onClick={handleLinkClick('https://www.ace.aaa.com/legal/terms-of-use.html')}
        >
          Terms & Conditions
        </Link>
        , and{' '}
        <Link
          id="text-terms-link"
          component="a"
          onClick={handleLinkClick('https://www.ace.aaa.com/legal/text-messaging-programs-terms-and-conditions.html')}
        >
          Text Terms
        </Link>
        .
      </Typography>
    </div>
  );
};

export default UpdateEmailsForm;
